// The version history column (#11523, reshaped in #11819): a right-hand
// pane BESIDE the artifact — the Google-Drive shape — listing every
// snapshot newest first. The head of the list is the current version; older
// entries restore; AI-produced entries link to the turn or run that made
// them. The pane host renders it in its body row while the history store
// says it is open, and hides its other header actions meanwhile.
import React, { useEffect, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  restoreArtifactVersion,
  type ArtifactItem,
  type ArtifactVersion,
} from "../FpPages/Artifacts/useArtifacts";
import { toConversationSlug, toTurnSlug } from "../FpPages/Chat/chatUtils";
import { closeArtifactHistory } from "./artifactHistory";
import {
  useArtifactVersions,
  useInvalidateArtifactVersions,
} from "./artifactVersions";
import { hasOpenNestedInteraction } from "./documentViewerEscape";

interface ArtifactVersionHistoryPanelProps {
  artifactId: string;
  /** The past version the pane is showing, or null for the live content:
   *  the list's selected row (#12481). The artifact panes own it (their
   *  header's Back to latest returns to the live content while the column
   *  is open); the document viewer leaves it out, and the column keeps the
   *  selection itself. */
  viewing?: number | null;
  /** Called with the picked version so the host can show its snapshot —
   *  or with null when the current version is picked, so the host shows
   *  the live content again (#11819). */
  onView: (version: ArtifactVersion | null) => void;
  /** Called with the version that was just made current and the refreshed
   *  artifact, after the restore succeeded server-side. A cross-type
   *  restore renames the artifact, so hosts showing its name must update
   *  from the refreshed item. */
  onRestored: (version: ArtifactVersion, artifact: ArtifactItem) => void;
  /** Surfaces a failed restore in the host's own error area. */
  onError?: (message: string) => void;
}

const SOURCE_LABELS: Record<string, string> = {
  chat: "Chat",
  agent_run: "Agent",
  hand_edit: "Hand edit",
  restore: "Restore",
};

const versionWhen = (version: ArtifactVersion): string => {
  const at = new Date(version.updated_at);
  return `${at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })} ${at.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

/** Where an AI-produced version came from, as an in-app path (#11819): a
 *  chat version lands on its turn (the `?turn=` anchor the chat page already
 *  honors), an agent-run version on the run's conversation — the same URL
 *  Chat uses. Null for hand edits, restores, and versions whose
 *  provenance is incomplete. */
export const versionOriginPath = (version: ArtifactVersion): string | null => {
  if (version.source_kind === "chat" && version.source_conversation_id) {
    const turn = version.source_turn_id
      ? `?turn=${encodeURIComponent(toTurnSlug(version.source_turn_id))}`
      : "";
    return `/chat/${toConversationSlug(version.source_conversation_id)}${turn}`;
  }
  if (
    version.source_kind === "agent_run" &&
    version.source_agent_id &&
    version.source_run_id &&
    version.source_conversation_id
  ) {
    return (
      `/agents/${version.source_agent_id}/runs/${version.source_run_id}` +
      `/conversations/${version.source_conversation_id}`
    );
  }
  return null;
};

const ArtifactVersionHistoryPanel: React.FC<
  ArtifactVersionHistoryPanelProps
> = ({ artifactId, viewing, onView, onRestored, onError }) => {
  const navigate = useNavigate();
  // The one react-query versions read (#12481). A failed read must
  // not read as "No versions yet".
  const { versions: loaded, failed: loadFailed } =
    useArtifactVersions(artifactId);
  const invalidateVersions = useInvalidateArtifactVersions();
  const loading = loaded === null;
  const versions = loaded ?? [];
  // The selected row: the version on screen, else the head, which IS the
  // current version (#11819) — the list's selected row is the only "you
  // are here". Kept here only for a host that hands none in.
  const [ownViewing, setOwnViewing] = useState<number | null>(null);
  const selected =
    (viewing === undefined ? ownViewing : viewing) ??
    versions[0]?.version_number ??
    null;

  // Escape closes the history FIRST (#11819) and is consumed: the pane's own
  // Escape and the chat's stop shortcut both stand down on a prevented
  // event, so one keystroke never closes the history and the pane behind
  // it. Capture phase, because those handlers listen on document too and
  // registration order is not a contract. A nested dialog keeps its own
  // Escape.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (hasOpenNestedInteraction(document)) return;
      event.preventDefault();
      closeArtifactHistory();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  const restore = async (version: ArtifactVersion) => {
    try {
      const artifact = await restoreArtifactVersion(
        artifactId,
        version.version_number
      );
      // The restore is itself a new version: both readers re-read.
      invalidateVersions(artifactId);
      closeArtifactHistory();
      onRestored(version, artifact);
    } catch (err: unknown) {
      console.error("[Artifacts] restore failed", err);
      onError?.(
        `Restoring v${version.version_number} failed — the artifact is unchanged`
      );
    }
  };

  return (
    <aside
      aria-label="Version history"
      className="flex w-[272px] flex-none flex-col border-l border-[#e7e5e4] bg-white"
    >
      <div className="flex h-12 flex-none items-center justify-between border-b border-[#e7e5e4] px-4">
        <span className="text-[13.5px] font-semibold text-[#1c1917]">
          Version history
        </span>
        <Tooltip title="Close version history" arrow placement="bottom">
          <button
            type="button"
            aria-label="Close version history"
            onClick={closeArtifactHistory}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[7px] text-[#a8a29e] hover:bg-[#f5f5f4] hover:text-[#44403c]"
          >
            <X className="h-4 w-4" />
          </button>
        </Tooltip>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        {loading && (
          <div className="px-4 py-2 text-[13px] text-[#78716c]">Loading…</div>
        )}
        {!loading && loadFailed && (
          <div className="px-4 py-2 text-[13px] text-[#78716c]">
            Couldn't load versions — try again
          </div>
        )}
        {!loading && !loadFailed && versions.length === 0 && (
          <div className="px-4 py-2 text-[13px] text-[#78716c]">
            No versions yet
          </div>
        )}
        {!loading &&
          versions.map((version, index) => {
            // Newest first, so the head IS the current version (#11819):
            // labelled as such and never restorable — restoring the live
            // state would only append a version.
            const current = index === 0;
            const origin = versionOriginPath(version);
            const pick = () => {
              // Picking the current version shows the live content; a past
              // one shows its snapshot. The host's state is the selection
              // when it hands one in.
              setOwnViewing(current ? null : version.version_number);
              onView(current ? null : version);
            };
            return (
              <div
                key={version.version_number}
                role="button"
                tabIndex={0}
                aria-current={
                  selected === version.version_number ? "true" : undefined
                }
                onClick={pick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    pick();
                  }
                }}
                className={`group mx-2 flex cursor-pointer items-start justify-between gap-2 rounded-[8px] px-2.5 py-2 ${
                  selected === version.version_number
                    ? "bg-[var(--fp-ink-wash)]"
                    : "hover:bg-[var(--fp-hover)]"
                }`}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-[#1c1917]">
                    <span>
                      v{version.version_number} ·{" "}
                      {SOURCE_LABELS[version.source_kind] ??
                        version.source_kind}
                    </span>
                    {current && (
                      <span className="rounded-[5px] bg-[#f5f5f4] px-1.5 py-px text-[10.5px] font-medium uppercase tracking-[0.04em] text-[#78716c]">
                        Current version
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-[#a8a29e]">
                    {versionWhen(version)}
                  </div>
                  {version.filename && (
                    // The version's own filename disambiguates eras across
                    // a type conversion (report.docx → report.pdf).
                    <div className="mt-0.5 truncate text-[11.5px] text-[#78716c]">
                      {version.filename}
                    </div>
                  )}
                </div>
                <div className="flex flex-none items-center gap-1">
                  {origin && (
                    // An AI-produced version links to where it came from —
                    // the chat turn or the agent run (#11819).
                    <Tooltip
                      title={
                        version.source_kind === "agent_run"
                          ? "Open the agent run"
                          : "Open the chat turn"
                      }
                      arrow
                      placement="bottom"
                    >
                      <button
                        type="button"
                        aria-label={`Open origin of v${version.version_number}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(origin);
                        }}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-[#a8a29e] hover:bg-white hover:text-[#44403c]"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                    </Tooltip>
                  )}
                  {!current && (
                    <button
                      type="button"
                      aria-label={`Restore v${version.version_number}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void restore(version);
                      }}
                      className="flex-none rounded-md border border-[#e7e5e4] px-2 py-0.5 text-xs text-[#44403c] opacity-0 hover:bg-white focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      Restore
                    </button>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </aside>
  );
};

export default ArtifactVersionHistoryPanel;
